import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Loader2,
  MessageSquare,
  User,
  Mail,
  Calendar,
  CheckCircle,
  FileText,
  Globe,
  Shield,
} from 'lucide-react';
import { getSupportMessages, updateSupportMessage } from '../../services/api';

const formatDateTime = (isoString) => {
  const date = new Date(isoString);
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getTypeIcon = (type) => {
  switch (type) {
    case 'public':
      return <Globe className="h-4 w-4" />;
    case 'member':
      return <Shield className="h-4 w-4" />;
    case 'minute_comment':
      return <FileText className="h-4 w-4" />;
    default:
      return <MessageSquare className="h-4 w-4" />;
  }
};

const getTypeLabel = (type) => {
  switch (type) {
    case 'public':
      return 'Public Support';
    case 'member':
      return 'Member Support';
    case 'minute_comment':
      return 'Minute Comment';
    default:
      return type;
  }
};

const getTypeBadgeVariant = (type) => {
  switch (type) {
    case 'public':
      return 'secondary';
    case 'member':
      return 'default';
    case 'minute_comment':
      return 'outline';
    default:
      return 'secondary';
  }
};

const AdminSupportPage = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [updatingId, setUpdatingId] = useState(null);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await getSupportMessages();
      setMessages(response.data.messages);
    } catch (error) {
      console.error('Failed to fetch support messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkDealtWith = async (message) => {
    const messageId = message.SK || message.id;
    const messagePk = message.PK;
    setUpdatingId(messageId);
    try {
      await updateSupportMessage(messageId, messagePk, 'Dealt With');
      setMessages((prev) =>
        prev.map((msg) => {
          const msgId = msg.SK || msg.id;
          return msgId === messageId ? { ...msg, status: 'Dealt With' } : msg;
        })
      );
    } catch (error) {
      console.error('Failed to update message:', error);
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredMessages = messages.filter((msg) => {
    if (statusFilter !== 'all' && msg.status !== statusFilter) return false;
    if (typeFilter !== 'all' && msg.type !== typeFilter) return false;
    return true;
  });

  const openCount = messages.filter((m) => m.status === 'Open').length;

  if (loading) {
    return (
      <div className="p-6 md:p-12 flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  return (
    <div className="p-6 md:p-12" data-testid="admin-support-page">
      <div className="max-w-4xl">
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-bold text-gray-900">Support Messages</h1>
          <p className="text-gray-600 mt-1">
            View and manage all support requests from members and the public.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className="border-gray-100">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{messages.length}</p>
                <p className="text-sm text-gray-500">Total Messages</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-gray-100">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-red-100 flex items-center justify-center">
                <MessageSquare className="h-5 w-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{openCount}</p>
                <p className="text-sm text-gray-500">Open</p>
              </div>
            </CardContent>
          </Card>
          <Card className="border-gray-100">
            <CardContent className="p-4 flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{messages.length - openCount}</p>
                <p className="text-sm text-gray-500">Dealt With</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-gray-100 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Status</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid="status-filter">
                    <SelectValue placeholder="Filter by status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="Open">Open</SelectItem>
                    <SelectItem value="Dealt With">Dealt With</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1">
                <label className="text-sm font-medium text-gray-700 mb-1 block">Type</label>
                <Select value={typeFilter} onValueChange={setTypeFilter}>
                  <SelectTrigger data-testid="type-filter">
                    <SelectValue placeholder="Filter by type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="public">Public Support</SelectItem>
                    <SelectItem value="member">Member Support</SelectItem>
                    <SelectItem value="minute_comment">Minute Comments</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Messages List */}
        <div className="space-y-4" data-testid="support-messages-list">
          {filteredMessages.length === 0 ? (
            <Card className="border-gray-100">
              <CardContent className="p-8 text-center">
                <MessageSquare className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500">No messages found matching your filters.</p>
              </CardContent>
            </Card>
          ) : (
            filteredMessages.map((message) => (
              <Card
                key={message.SK || message.id}
                className={`border-gray-100 ${
                  message.status === 'Open' ? 'border-l-4 border-l-amber-500' : ''
                }`}
                data-testid={`support-message-${message.SK || message.id}`}
              >
                <CardContent className="p-5">
                  {/* Header */}
                  <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
                    <div className="flex items-center gap-3">
                      <Badge variant={getTypeBadgeVariant(message.type)} className="flex items-center gap-1">
                        {getTypeIcon(message.type)}
                        {getTypeLabel(message.type)}
                      </Badge>
                      {message.verified ? (
                        <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">
                          <CheckCircle className="h-3 w-3 mr-1" />
                          Verified
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="bg-gray-50 text-gray-600">
                          Unverified
                        </Badge>
                      )}
                    </div>
                    <Badge variant={message.status === 'Open' ? 'destructive' : 'secondary'}>
                      {message.status}
                    </Badge>
                  </div>

                  {/* Sender Info */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-6 mb-4 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      {message.name}
                    </div>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4" />
                      {message.email}
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4" />
                      {formatDateTime(message.createdAt)}
                    </div>
                  </div>

                  {/* Minute Reference (if applicable) */}
                  {message.type === 'minute_comment' && message.minuteTitle && (
                    <div className="mb-4 p-3 bg-gray-50 rounded-md text-sm">
                      <span className="text-gray-500">Regarding:</span>{' '}
                      <span className="font-medium text-gray-900">{message.minuteTitle}</span>
                    </div>
                  )}

                  {/* Message Body */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <p className="text-gray-700 whitespace-pre-wrap">{message.message}</p>
                  </div>

                  {/* Actions */}
                  {message.status === 'Open' && (
                    <div className="flex justify-end">
                      <Button
                        onClick={() => handleMarkDealtWith(message)}
                        disabled={updatingId === (message.SK || message.id)}
                        className="bg-emerald-600 hover:bg-emerald-700"
                        data-testid={`mark-dealt-btn-${message.SK || message.id}`}
                      >
                        {updatingId === message.id ? (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        ) : (
                          <CheckCircle className="mr-2 h-4 w-4" />
                        )}
                        Mark as Dealt With
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSupportPage;
